import type { StreamingCommand } from '../../aiStreamingOrchestratorPluginType';

/**
 * Returns the set of `localId`s whose disappearance from the top-level
 * document snapshot after a streaming command's apply is *expected*.
 * These nodes are excluded from the `unexpected_node_removal` diagnostic
 * so the event only fires for genuinely collateral removals.
 *
 * The set is deliberately **over-inclusive** for v1 — false negatives
 * (missed unexpected removals) are preferable to false positives
 * (spurious noise) until dashboards are built and we can measure the
 * actual signal-to-noise ratio. Specifically:
 *
 * - `removeNode`  — removes a single node by `localId`. The localId
 *                   should disappear.
 * - `removeNodes` — removes a list of nodes by `localIds`. All listed
 *                   localIds should disappear.
 * - `moveNode`   — relocates a node. Its `localId` may legitimately
 *                   leave the top-level snapshot if the move target is
 *                   nested.
 * - `replaceNode` — replaces a node's content. The targeted `localId`
 *                   may or may not be preserved on the replacement node
 *                   depending on downstream apply logic. Suppress the
 *                   diagnostic to avoid false positives whenever the
 *                   localId is not preserved (the common
 *                   "replace this paragraph with new content" path
 *                   from the AI legitimately drops the original
 *                   localId).
 * - `wrap` / `wrapNode` — wrap the targeted node inside a new node.
 *                   If the wrapper sits at top level, the wrapped
 *                   node's `localId` leaves the top-level snapshot
 *                   (it's now nested). Suppress the diagnostic to
 *                   avoid false positives for "wrap this in a panel"
 *                   etc.
 *
 * Trade-off: a `replaceNode` / `wrap` that *also* drops an unrelated
 * top-level neighbour will silently swallow that collateral removal
 * for the targeted localId only — the neighbour's localId is still
 * detected. Acceptable for v1.
 *
 * If new removal-style commands are added to `StreamingCommand` in the
 * future, extend this function so the diagnostic does not start firing
 * spuriously for them.
 */
export function extractExplicitRemovalTargets(command: StreamingCommand): Set<string> {
	const ids = new Set<string>();
	switch (command.type) {
		case 'removeNode':
		case 'moveNode':
		case 'replaceNode':
			ids.add(command.localId);
			break;
		case 'removeNodes':
			for (const id of command.localIds) {
				ids.add(id);
			}
			break;
		case 'wrap':
		case 'wrapNode':
			ids.add(command.localId);
			break;
		default:
			// All other commands either target a single node by `localId`
			// without removing it (insertNodeBefore / insertNodeAfter /
			// setAttrs) or have no target at all (appendNode /
			// appendNodeToEnd).
			break;
	}
	return ids;
}
